export enum MeetingStatus {
  Pending = 'pending',
  Validating = 'validating',
  Validated = 'validated',
  Invalid = 'invalid',
  EditRequested = 'edit_requested',
  Booked = 'booked',
  Cancelled = 'cancelled',
  Recording = 'recording',
  Failed = 'failed',
  Success = 'success',
  Expired = 'expired',
}

// Compliance review hold (MEA-18). An organization with compliance review on has every recording
// held until a compliance viewer approves or rejects it. null when the meeting is not under review.
// Kept apart from MeetingStatus, which keeps describing the recording: core sets "pending" when
// recording starts, so a recording meeting can already be pending.
export enum MeetingReviewStatus {
  Pending = 'pending',
  Approved = 'approved',
  Rejected = 'rejected',
}

export enum MeetingStage {
  Reviewing = 'reviewing',
  ReviewCompleted = 'review_completed',
  Recording = 'recording',
  AfterMeeting = 'after_meeting',
}

export enum MeetingType {
  ExpertNetworkCall = 'expert_network_call',
  MeetingWithManagement = 'meeting_with_management',
  SellSideResearch = 'sell_side_research',
  ForumWebinarConference = 'forum_webinar_conference',
  EarningsCall = 'earnings_call',
  Other = 'other',
  // Offered only to organizations whose meeting agent settings list it (MEA-39).
  BrokerHostedGeneralPresentation = 'broker_hosted_general_presentation',
}

export const MEETING_TYPE_LABELS: Record<MeetingType, string> = {
  [MeetingType.ExpertNetworkCall]: 'Expert Network Call',
  [MeetingType.MeetingWithManagement]: 'Meeting with Management',
  [MeetingType.SellSideResearch]: 'Sell-side Research',
  [MeetingType.ForumWebinarConference]: 'Forum / Webinar / Conference',
  [MeetingType.EarningsCall]: 'Earnings Call',
  [MeetingType.Other]: 'Other',
  [MeetingType.BrokerHostedGeneralPresentation]: 'Broker Hosted General Presentation',
}

export type MeetingTypeOption = { label: string; value: MeetingType }

// The list an organization without its own gets, and the fallback while its settings load:
// the default six, named here rather than derived so an organization-only type added to the
// enum never slips into it (mirrors core's DEFAULT_MEETING_TYPES, MEA-39).
export const DEFAULT_BOOKING_MEETING_TYPE_OPTIONS: MeetingTypeOption[] = [
  MeetingType.ExpertNetworkCall,
  MeetingType.MeetingWithManagement,
  MeetingType.SellSideResearch,
  MeetingType.ForumWebinarConference,
  MeetingType.EarningsCall,
  MeetingType.Other,
].map((value) => ({ label: MEETING_TYPE_LABELS[value], value }))

export const STAGE_STATUS_MAP = {
  [MeetingStage.Reviewing]: [
    MeetingStatus.Pending,
    MeetingStatus.Validating,
    MeetingStatus.Validated,
    MeetingStatus.Invalid,
  ],
  [MeetingStage.ReviewCompleted]: [
    MeetingStatus.EditRequested,
    MeetingStatus.Booked,
    MeetingStatus.Cancelled,
  ],
  [MeetingStage.Recording]: [MeetingStatus.Recording],
  [MeetingStage.AfterMeeting]: [MeetingStatus.Failed, MeetingStatus.Success, MeetingStatus.Expired],
}

export const POLICY_LIMIT_MINUTES = 30

export type MeetingInformation = {
  id: string
  message_id: string
  meeting_id: string
  link: string
  password_or_passcode: string
  meeting_date: string
  start_time: string
  end_time: string
  duration: number
  participant: string
  participant_affiliation: string
  participant_fullname?: string
  // Email the agent joins with, resolved at booking (LIN-193).
  user_email?: string | null
  speaker_language: string
  summary_language: string
  timezone: string
  status: MeetingStatus
  stage: MeetingStage
  content: string
  meeting_name: string
  summary_title: string
  platform: string
  created_at: string
  // Server-computed (show view): booked after the meeting had started (LIN-307)
  booked_after_start?: boolean
  updated_at: string
  invalid_type: MeetingInformationInvalidType
  cancel_reason: string
  meeting_type: MeetingType | null
  // Expert Call compliance fields (PRD #3260); server blueprinter returns them nullable.
  expert_name: string | null
  employer: string | null
  analyst_name: string | null
  // True when this row reached the viewer through a share rather than ownership
  // (LIN-306). Edit, cancel and share are owner-only, so the panel hides them.
  // Absent on payloads rendered without a viewer, which read as owned.
  shared_with_me?: boolean
  // Owner's name, present only when shared_with_me is true. Drives the
  // "Shared by <name>" label on the recipient's view.
  shared_by_name?: string | null
  // Compliance review hold (MEA-18). Missing only from a server older than MEA-18.
  review_status?: MeetingReviewStatus | null
}

// Matches core-server ExtractedMeetingDetails. duration / speaker_language /
// summary_language are not returned by extract_content — the form derives
// duration locally from start/end. expert_name / employer / analyst_name are
// Expert Call compliance fields (PRD #3260); the form skips auto-fill and
// prompts for manual entry when a value exceeds 30 chars.
export type ExtractMeetingInformation = {
  link: string
  password_or_passcode: string
  meeting_date: string
  start_time: string
  end_time: string
  participant: string
  timezone: string
  meeting_name: string
  meeting_id: string
  expert_name: string
  employer: string
  analyst_name: string
  // LLM-classified meeting type ('' if unclear). Applied only for compliance analysts.
  meeting_type: string
}

export type CreateMeetingInformationParams = {
  meeting_id?: string
  link: string
  password_or_passcode?: string
  meeting_date: string
  start_time: string
  end_time: string
  duration?: number
  participant?: string
  speaker_language: string
  summary_language: string
  timezone?: string
  status: MeetingStatus
  content?: string
}

export type ExtractMeetingContentParams = {
  content: string
}

export type BookMeetingParams = {
  meeting_name?: string
  meeting_id?: string
  platform: string
  link?: string
  password_or_passcode?: string
  meeting_date?: Date | null
  start_time?: string
  end_time?: string
  duration?: number | null
  participant?: string
  speaker_language?: string
  summary_language?: string
  timezone?: string
  status: MeetingStatus
  content?: string
  custom_guide?: string
  // Per-call override for the meeting agent display name. Omitted when the user
  // does not customize it, so the server resolves the default (user setting / fallback).
  participant_fullname?: string
  // Per-booking override for the email the agent joins with (LIN-193). Omitted when
  // untouched so the server resolves the default (role-gated; user setting / account email).
  user_email?: string
  meeting_type?: MeetingType
  // Expert Call compliance fields (PRD #3260). Sent only for compliance_analyst.
  expert_name?: string
  employer?: string
  analyst_name?: string
  // Members to share the result with, chosen at booking (LIN-306). Omitted when
  // none are picked. The server intersects this with the booker's organization,
  // so it is a request, not a grant.
  recipient_user_ids?: string[]
}

export type MessageMeetingInformation = {
  id: string
  message_id: string
  meeting_id: string
  platform: string
  meeting_name: string
  link: string
  password_or_passcode: string
  meeting_date: string
  start_time: string
  end_time: string
  duration: number
  participant: string
  speaker_language: string
  summary_language: string
  timezone: string
  status: MeetingStatus
  stage: MeetingStage
  content: string
  // Compliance review hold (MEA-18). Missing only from a server older than MEA-18.
  review_status?: MeetingReviewStatus | null
}

export type VerifyUpdateMeetingTokenParams = {
  id: string
  token: string
}

export type UpdatePublicMeetingParams = {
  id: string
  token: string
  link?: string
  meeting_id?: string
  password_or_passcode?: string
}

export type MeetingAgentEvent = {
  id: string
  meeting_name: string
  start_time: string
  end_time: string
  status: MeetingStatus
  link: string
  password_or_passcode: string
  participant: string
  participant_affiliation: string
  platform: string
  stage: MeetingStage
  timezone: string
  summary_language: string
  detected_language?: string
  has_transcript?: boolean
  has_original_transcript?: boolean
  recurring_meeting_id?: string
  meeting_type?: MeetingType | null
  // True when this meeting reached the viewer through a share (LIN-306).
  // Owner-only actions (cancel, edit, share) are hidden when it is set.
  shared_with_me?: boolean
  // Owner's name for the "Shared by <name>" label; present only for recipients.
  shared_by_name?: string | null
  // Compliance review hold (MEA-18). Missing only from a server older than MEA-18.
  review_status?: MeetingReviewStatus | null
}

export enum MeetingInformationInvalidType {
  Passcode = 'passcode',
  Registration = 'registration',
  MeetingLink = 'meeting_link',
  MeetingId = 'meeting_id',
  Others = 'others',
  AttemptExceeded = 'attempt_exceeded',
}

export type UpdateInvalidMeetingParams = {
  id: string
  link?: string
  password_or_passcode?: string
  meeting_id?: string
}

export type CancelMeetingParams = {
  id: string
  // Required by the server when a compliance viewer cancels someone else's meeting (MEA-18).
  reason?: string
}

export type MeetingUrlBlacklistEntry = {
  pattern: string
  /**
   * Block reason written by an admin for end users. Null when none has been written
   * yet — render nothing rather than substituting the internal `description` column,
   * which the server deliberately does not expose.
   */
  user_facing_reason: string | null
}

/**
 * GET core /meeting_informations/organization_settings (MEA-39): the caller's organization's
 * Meeting Agent settings with defaults applied. `restricted` is true when the organization
 * has its own meeting type list; `meeting_types` is what the booking form offers, in order.
 */
export type MeetingAgentOrganizationSettings = {
  restricted: boolean
  meeting_types: Array<{ key: MeetingType; label: string }>
}

export type MeetingUrlBlacklistResponse = {
  message: string
  /**
   * Prefer `entries`, which also carries each pattern's user-facing reason. Kept
   * (not removed) so clients released before `entries` existed keep blocking
   * through a rolling deploy.
   */
  patterns: string[]
  /** Absent on servers released before `entries` existed. */
  entries?: MeetingUrlBlacklistEntry[]
}

// A live grant on a meeting (LIN-306). Revoked grants are never returned.
export interface MeetingShareRecipient {
  id: string
  created_at: string
  recipient: {
    id: string
    email: string
    firstName: string | null
    lastName: string | null
  }
}
